import { PushPinOutlined } from "@mui/icons-material";
import classNames from "classnames";
import { SmallBody } from "helpers/TypographyComponents";
import type { DetailedHTMLProps, HtmlHTMLAttributes } from "react";

import type { TableColumn, TableRow } from "../types";
import Template from "./Template";

export type IRow = {
    data: TableRow;
    columns: TableColumn[];
    isTableCollapsed?: boolean;
    isPinned?: boolean;
} & DetailedHTMLProps<HtmlHTMLAttributes<HTMLTableRowElement>, HTMLTableRowElement>;

const alignmentStyles: Record<Exclude<TableColumn["align"], undefined>, string> = {
    right: "justify-end",
    left: "justify-left",
    center: "justify-center",
};

const textAlignmentStyles: Record<Exclude<TableColumn["align"], undefined>, string> = {
    right: "text-right",
    left: "text-left",
    center: "text-center",
};

const containerVariantStyles: Record<Exclude<TableRow["variant"], undefined>, string> = {
    default: "hover:bg-[#f8f8f8d6] text-black!",
    highlighted: "bg-[#646464] text-white!",
};

const textVariantStyles: Record<Exclude<TableRow["variant"], undefined>, string> = {
    default: "text-black!",
    highlighted: "text-white!",
};

function Row({ data, columns, isTableCollapsed, isPinned, className, onClick, ...trProps }: IRow) {
    const isCollapsed = isTableCollapsed && data.isPinnable && !isPinned;

    return (
        <Template
            columns={columns}
            onClick={onClick}
            cells={data.cells}
            className={classNames(
                "border-[#eaeaea]! transition-all",
                containerVariantStyles[data.variant || "default"],
                {
                    "cursor-pointer": !!onClick,
                },
                className,
            )}
            renderCell={({ cell, column, index }) => {
                return (
                    <div
                        className={`flex items-center gap-2 ${alignmentStyles[column.align || "left"]} ${
                            isCollapsed ? "py-0!" : "py-2!"
                        }`}
                    >
                        {data.isPinnable && index === 0 ? (
                            <div className="relative flex-none w-3.5! h-0!">
                                <PushPinOutlined
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        bottom: 0,
                                        transform: "translateY(50%)",
                                        width: "14px",
                                        height: "14px",
                                        opacity: isPinned ? 1 : 0.2,
                                    }}
                                />
                            </div>
                        ) : null}
                        <SmallBody
                            className={`${textVariantStyles[data.variant || "default"]} ${
                                textAlignmentStyles[column.align || "left"]
                            } whitespace-nowrap overflow-hidden text-ellipsis max-w-full`}
                        >
                            {cell.label || cell.value || "-"}
                        </SmallBody>
                    </div>
                );
            }}
            {...trProps}
        />
    );
}

export default Row;
